<template>
  <div>
    <div
      v-if="showPros"
      class="pros-horizontal"
      :class="{'pros-optimization': shoppableRedesignOptimization }"
    >
      <ProsCollection
        :recommendation-config="recommendationConfig"
        :panel-config="getProsConfig()"
        @dataLoadStatusEvent="loader"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { ProsCollection } from '@common-vue/pros-collection';
import prosCollectionComponentType from '@common-vue/pros-collection/src/utils/prosCollectionComponentType';
import { usePageData } from '@composables/usePageData';
import { useBrand } from '@composables/useBrand';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useFeatureEligibility } from '@modules/featureControl';
import useSelectedMemberId from '@modules/latchkey/composables/useSelectedMemberId';
import useColor from '@modules/color/composables/useColor';
import useProsLoader from '@composables/utils/useProsLoader';

const { isBcom } = useBrand();
const {
  prosInNewTab, isShoppable, shoppableRedesignOptimization,
} = useFeatureEligibility();

const { taxonomy } = useCommonProductGetters();
const categoryId = taxonomy.value.defaultCategoryId;

const { productId, product, killswitches } = usePageData();
const { detail, traits, imagery } = product?.product;
const selectedMemberId = useSelectedMemberId();
const { selectedColor } = useColor();
const { showPros, loader } = useProsLoader();

const recommendationConfig = computed(() => ({
  zone: isShoppable ? 'PDP_ZONE_G' : 'PDP_ZONE_A',
  productId,
  memberProductId: selectedMemberId.value,
  categoryId,
  selectedColor: selectedColor.value?.name,
  selectedColorFamily: selectedColor.value?.normalName,
}));
const baseConfig = {
  openInNewTab: prosInNewTab,
  properties: killswitches,
  meta: {
    product: {
      id: productId,
      detail,
      traits,
      imagery,
    },
  },
};

const getProsConfig = () => ({
  ...baseConfig,
  componentType: prosCollectionComponentType.PROS_CAROUSEL,
  numberOfItems: {
    small: 2.4,
    medium: 3,
    large: 4,
  },
  customHeaderType: (shoppableRedesignOptimization && isBcom) ? 'h3' : 'h4',
});

</script>

<style lang="scss" scoped>
@import "~settingSCSS";
@include breakpoint(large) {
  .prosAWrapper {
    min-width: rem-calc(102);
    width: rem-calc(102);
  }
}
.pros-horizontal.pros-optimization {
  :deep(.carousel-container) {
    .title-container {
      @include margin-bottom-s;
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@include breakpoint(medium) {
  .pros-horizontal {
    :deep(.brand-and-name) {
      :nth-child(2) {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        line-clamp: 1;
      }
    }
    :deep(.product-brand) {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      line-clamp: 1;
    }
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@include breakpoint(medium) {
  .pros-horizontal {
    :deep(.product-brand) {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}
</style>
